import type { CollectionSnapshot } from "../api/types";
import { COLLECTION_SNAPSHOT_SCHEMA_VERSION } from "../portability/constants";

type StoredCollectionSnapshot = {
  schemaVersion: typeof COLLECTION_SNAPSHOT_SCHEMA_VERSION;
  accountId: string;
  savedAt: string;
  snapshot: CollectionSnapshot;
};

function storageKey(accountId: string): string {
  return `hw.collection.snapshot.v${COLLECTION_SNAPSHOT_SCHEMA_VERSION}.${accountId}`;
}

function isCollectionSnapshot(value: unknown): value is CollectionSnapshot {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return typeof record.revision === "number" && Array.isArray(record.items);
}

/**
 * Account-scoped last-known collection snapshot for offline read-only UI.
 * Not authoritative — backend remains the sole write authority.
 */
export function saveCollectionSnapshot(
  accountId: string,
  snapshot: CollectionSnapshot,
  storage: Storage = localStorage,
): void {
  const payload: StoredCollectionSnapshot = {
    schemaVersion: COLLECTION_SNAPSHOT_SCHEMA_VERSION,
    accountId,
    savedAt: new Date().toISOString(),
    snapshot,
  };
  storage.setItem(storageKey(accountId), JSON.stringify(payload));
}

export function loadCollectionSnapshot(
  accountId: string,
  storage: Storage = localStorage,
): { snapshot: CollectionSnapshot; savedAt: string } | null {
  const raw = storage.getItem(storageKey(accountId));
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return null;
    }
    const record = parsed as Record<string, unknown>;
    if (
      record.schemaVersion !== COLLECTION_SNAPSHOT_SCHEMA_VERSION ||
      record.accountId !== accountId ||
      typeof record.savedAt !== "string" ||
      !isCollectionSnapshot(record.snapshot)
    ) {
      return null;
    }
    return { snapshot: record.snapshot, savedAt: record.savedAt };
  } catch {
    return null;
  }
}

export function clearCollectionSnapshot(
  accountId: string,
  storage: Storage = localStorage,
): void {
  storage.removeItem(storageKey(accountId));
}

/** Remove all Gate 9 private collection snapshot keys (family-device logout policy). */
export function clearAllCollectionSnapshots(storage: Storage = localStorage): void {
  const prefix = `hw.collection.snapshot.v${COLLECTION_SNAPSHOT_SCHEMA_VERSION}.`;
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key?.startsWith(prefix)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    storage.removeItem(key);
  }
}
